/**
 * Permission scope
 */
export var AccessTokenCreateRequestBodyScope;
(function (AccessTokenCreateRequestBodyScope) {
    AccessTokenCreateRequestBodyScope["Admin"] = "admin";
    AccessTokenCreateRequestBodyScope["ReadOnly"] = "read_only";
    AccessTokenCreateRequestBodyScope["ReadWrite"] = "read_write";
})(AccessTokenCreateRequestBodyScope || (AccessTokenCreateRequestBodyScope = {}));
/**
 * When given, adds a capped `total_count` (`total_count_cap_exceeded` is true when the cap
 * is reached) to meta.
 *
 * Boolean indicating whether a block is active.
 *
 * Boolean indicating whether a customer has any pending or active mandates.
 *
 * Boolean value indicating whether the API should return tax data for the cutover period of
 * April to August 2020. Defaults to false.
 */
export var Include2020_TaxCutover;
(function (Include2020_TaxCutover) {
    Include2020_TaxCutover["False"] = "false";
    Include2020_TaxCutover["True"] = "true";
})(Include2020_TaxCutover || (Include2020_TaxCutover = {}));
/**
 * The type of business of the creditor. Currently, `individual`, `company`, `charity`,
 * `partnership`, and `trust` are supported.
 *
 * Creditor type
 */
export var CreditorType;
(function (CreditorType) {
    CreditorType["Charity"] = "charity";
    CreditorType["Company"] = "company";
    CreditorType["Individual"] = "individual";
    CreditorType["Partnership"] = "partnership";
    CreditorType["Trust"] = "trust";
})(CreditorType || (CreditorType = {}));
/**
 * The charity's type (deprecated - use creditor_subtype instead)
 */
export var CharityType;
(function (CharityType) {
    CharityType["Casc"] = "casc";
    CharityType["ExceptedScoutOrGuideGroup"] = "excepted_scout_or_guide_group";
    CharityType["Other"] = "other";
    CharityType["RegisteredCharity"] = "registered_charity";
    CharityType["UnregisteredSportsClub"] = "unregistered_sports_club";
})(CharityType || (CharityType = {}));
export var CompanyType;
(function (CompanyType) {
    CompanyType["Corporation"] = "corporation";
    CompanyType["LimitedLiabilityCompany"] = "limited_liability_company";
})(CompanyType || (CompanyType = {}));
/**
 * The AML entity's type
 */
export var ControlProngType;
(function (ControlProngType) {
    ControlProngType["Person"] = "person";
})(ControlProngType || (ControlProngType = {}));
/**
 * The partnership's type (deprecated - use creditor_subtype instead)
 */
export var PartnershipType;
(function (PartnershipType) {
    PartnershipType["Blended"] = "blended";
    PartnershipType["Gbr"] = "gbr";
    PartnershipType["GeneralPartnership"] = "general_partnership";
    PartnershipType["Kg"] = "kg";
    PartnershipType["LimitedPartnership"] = "limited_partnership";
    PartnershipType["Ohg"] = "ohg";
    PartnershipType["UnlimitedPartnership"] = "unlimited_partnership";
})(PartnershipType || (PartnershipType = {}));
/**
 * The AML entity's type
 */
export var TrusteeType;
(function (TrusteeType) {
    TrusteeType["Company"] = "company";
    TrusteeType["Person"] = "person";
})(TrusteeType || (TrusteeType = {}));
/**
 * The status of the authorisation
 */
export var AppAuthorisationsStatus;
(function (AppAuthorisationsStatus) {
    AppAuthorisationsStatus["Active"] = "active";
    AppAuthorisationsStatus["Revoked"] = "revoked";
})(AppAuthorisationsStatus || (AppAuthorisationsStatus = {}));
/**
 * One of:
 * - `created`: the app has been created, and hasn't yet been submitted for review
 * - `pending_review`: the app has been submitted for review
 * - `live`: the app has been approved, and it's accepting connections from merchants
 * - `disabled`: the app has been disabled and cannot be connected to, or reinstated
 */
export var AppsStatus;
(function (AppsStatus) {
    AppsStatus["Created"] = "created";
    AppsStatus["Disabled"] = "disabled";
    AppsStatus["Live"] = "live";
    AppsStatus["PendingReview"] = "pending_review";
})(AppsStatus || (AppsStatus = {}));
/**
 * The type of the authentication method.
 *
 * Filter authentication factors by type.
 */
export var FactorType;
(function (FactorType) {
    FactorType["Sms"] = "sms";
    FactorType["Totp"] = "totp";
})(FactorType || (FactorType = {}));
/**
 * The status of the authentication factor.
 *
 * Filter authentication factors by status.
 */
export var AuthenticationFactorsStatus;
(function (AuthenticationFactorsStatus) {
    AuthenticationFactorsStatus["Disabled"] = "disabled";
    AuthenticationFactorsStatus["Pending"] = "pending";
    AuthenticationFactorsStatus["Verified"] = "verified";
})(AuthenticationFactorsStatus || (AuthenticationFactorsStatus = {}));
export var AuthorisationRequestsStatus;
(function (AuthorisationRequestsStatus) {
    AuthorisationRequestsStatus["GcInvalid"] = "gc_invalid";
    AuthorisationRequestsStatus["Invalid"] = "invalid";
    AuthorisationRequestsStatus["Pending"] = "pending";
})(AuthorisationRequestsStatus || (AuthorisationRequestsStatus = {}));
/**
 * [ISO 4217](http://en.wikipedia.org/wiki/ISO_4217#Active_codes) currency code. Currently
 * "AUD", "CAD", "DKK", "EUR", "GBP", "NZD", "SEK" and "USD" are supported.
 *
 * The currency of the scheme identifier.
 *
 * organisations default/home currency
 *
 * The currency that the limit applies to.
 */
export var SourceCurrencyEnum;
(function (SourceCurrencyEnum) {
    SourceCurrencyEnum["Aud"] = "AUD";
    SourceCurrencyEnum["Cad"] = "CAD";
    SourceCurrencyEnum["Dkk"] = "DKK";
    SourceCurrencyEnum["Eur"] = "EUR";
    SourceCurrencyEnum["Gbp"] = "GBP";
    SourceCurrencyEnum["Nzd"] = "NZD";
    SourceCurrencyEnum["Sek"] = "SEK";
    SourceCurrencyEnum["Usd"] = "USD";
})(SourceCurrencyEnum || (SourceCurrencyEnum = {}));
/**
 * Type of the balance. Could be one of
 * <ul>
 * <li>pending_payments_submitted: Payments we have submitted to the scheme but not yet
 * confirmed. This does not exactly correspond to <i>Pending payments</i> in the dashboard,
 * because this balance does not include payments that are pending submission.</li>
 * <li>confirmed_funds: Payments that have been confirmed minus fees and unclaimed debits
 * for refunds, failures and chargebacks. These funds have not yet been moved into a
 * payout.</li>
 * <li>pending_payouts: Confirmed payments that have been moved into a payout. This is the
 * total due to be paid into your bank account in the next payout run (payouts happen once
 * every business day).
 * pending_payouts will only be non-zero while we are generating and submitting the payouts
 * to our partner bank.</li>
 * </ul>
 */
export var BalanceType;
(function (BalanceType) {
    BalanceType["ConfirmedFunds"] = "confirmed_funds";
    BalanceType["PendingPaymentsSubmitted"] = "pending_payments_submitted";
    BalanceType["PendingPayouts"] = "pending_payouts";
})(BalanceType || (BalanceType = {}));
/**
 * Type of authorisation, can be either 'mandate' or 'payment'.
 *
 * What type of bank authorisations are supported on this billing request
 *
 * Whether a refund was issued against a mandate or a payment. One of:
 * <ul>
 * <li>`payment`: <em>default</em> returns refunds created against payments only</li>
 * <li>`mandate`: returns refunds created against mandates only</li>
 * </ul>
 */
export var Type;
(function (Type) {
    Type["Mandate"] = "mandate";
    Type["Payment"] = "payment";
})(Type || (Type = {}));
/**
 * A bank payment scheme. Currently "ach", "autogiro", "bacs", "becs", "becs_nz",
 * "betalingsservice", "faster_payments", "pad", "pay_to" and "sepa_core" are supported.
 *
 * The scheme of the mandates to be imported.<br>All mandates in a single mandate
 * import must be for the same scheme.
 */
export var AvailableDebitSchemeEnum;
(function (AvailableDebitSchemeEnum) {
    AvailableDebitSchemeEnum["Ach"] = "ach";
    AvailableDebitSchemeEnum["Autogiro"] = "autogiro";
    AvailableDebitSchemeEnum["Bacs"] = "bacs";
    AvailableDebitSchemeEnum["Becs"] = "becs";
    AvailableDebitSchemeEnum["BecsNz"] = "becs_nz";
    AvailableDebitSchemeEnum["Betalingsservice"] = "betalingsservice";
    AvailableDebitSchemeEnum["FasterPayments"] = "faster_payments";
    AvailableDebitSchemeEnum["Pad"] = "pad";
    AvailableDebitSchemeEnum["PayTo"] = "pay_to";
    AvailableDebitSchemeEnum["SepaCore"] = "sepa_core";
})(AvailableDebitSchemeEnum || (AvailableDebitSchemeEnum = {}));
/**
 * A bank payment scheme that is supported by Success+. Currently "ach", "autogiro", "bacs",
 * "becs", "becs_nz", "pad" and "sepa_core" are supported. "betalingsservice" is not
 * supported but was added here to allow us to show other schemes in the Success+
 * configuration page when the organisation's home scheme is betalingsservice.
 */
export var SchemeScheme;
(function (SchemeScheme) {
    SchemeScheme["Ach"] = "ach";
    SchemeScheme["Autogiro"] = "autogiro";
    SchemeScheme["Bacs"] = "bacs";
    SchemeScheme["Becs"] = "becs";
    SchemeScheme["BecsNz"] = "becs_nz";
    SchemeScheme["Betalingsservice"] = "betalingsservice";
    SchemeScheme["Pad"] = "pad";
    SchemeScheme["SepaCore"] = "sepa_core";
})(SchemeScheme || (SchemeScheme = {}));
/**
 * Which authorisation adapter will be used to power these authorisations (GoCardless
 * internal use only)
 */
export var Adapter;
(function (Adapter) {
    Adapter["BankPayRecurring"] = "bank_pay_recurring";
    Adapter["BankidAis"] = "bankid_ais";
    Adapter["OpenBankingGatewayAis"] = "open_banking_gateway_ais";
    Adapter["OpenBankingGatewayPis"] = "open_banking_gateway_pis";
    Adapter["PlaidAis"] = "plaid_ais";
})(Adapter || (Adapter = {}));
/**
 * Describes whether we inferred the institution from the provided bank account details. One
 * of:
 * - `not_needed`: we won't attempt to infer the institution as it is not needed. Either
 * because it was manually selected or the billing request does not support this feature
 * - `pending`: we are waiting on the bank details in order to infer the institution
 * - `failed`: we weren't able to infer the institution
 * - `success`: we inferred the institution and added it to the resources of a Billing
 * Request
 */
export var InstitutionGuessStatus;
(function (InstitutionGuessStatus) {
    InstitutionGuessStatus["Failed"] = "failed";
    InstitutionGuessStatus["NotNeeded"] = "not_needed";
    InstitutionGuessStatus["Pending"] = "pending";
    InstitutionGuessStatus["Success"] = "success";
})(InstitutionGuessStatus || (InstitutionGuessStatus = {}));
/**
 * Status of the action
 */
export var ActionStatus;
(function (ActionStatus) {
    ActionStatus["Completed"] = "completed";
    ActionStatus["Pending"] = "pending";
})(ActionStatus || (ActionStatus = {}));
/**
 * Unique identifier for the action.
 */
export var ActionType;
(function (ActionType) {
    ActionType["BankAuthorisation"] = "bank_authorisation";
    ActionType["ChooseCurrency"] = "choose_currency";
    ActionType["CollectAmount"] = "collect_amount";
    ActionType["CollectBankAccount"] = "collect_bank_account";
    ActionType["CollectCustomerDetails"] = "collect_customer_details";
    ActionType["ConfirmPayerDetails"] = "confirm_payer_details";
    ActionType["SelectInstitution"] = "select_institution";
})(ActionType || (ActionType = {}));
/**
 * The unit of time between customer charge dates. One of `weekly`, `monthly` or `yearly`.
 *
 * The unit of time between customer charge dates. Either `weekly`, `monthly` or `yearly`.
 */
export var IntervalUnit;
(function (IntervalUnit) {
    IntervalUnit["Monthly"] = "monthly";
    IntervalUnit["Weekly"] = "weekly";
    IntervalUnit["Yearly"] = "yearly";
})(IntervalUnit || (IntervalUnit = {}));
/**
 * This field is ACH specific, sometimes referred to as [SEC
 * code](https://www.moderntreasury.com/learn/sec-codes).
 *
 * This is the way that the payer gives authorisation to the merchant.
 * web: Authorisation is Internet Initiated or via Mobile Entry (maps to SEC code: WEB)
 * telephone: Authorisation is provided orally over telephone (maps to SEC code: TEL)
 * paper: Authorisation is provided in writing and signed, or similarly authenticated (maps
 * to SEC code: PPD)
 */
export var AuthorisationSource;
(function (AuthorisationSource) {
    AuthorisationSource["Paper"] = "paper";
    AuthorisationSource["Telephone"] = "telephone";
    AuthorisationSource["Web"] = "web";
})(AuthorisationSource || (AuthorisationSource = {}));
/**
 * The alignment of the period.
 *
 * `calendar` - this will finish on the end of the current period. For example this will
 * expire on the Monday for the current week or the January for the next year.
 *
 * `creation_date` - this will finish on the next instance of the current period. For
 * example Monthly it will expire on the same day of the next month, or yearly the same day
 * of the next year.
 */
export var Alignment;
(function (Alignment) {
    Alignment["Calendar"] = "calendar";
    Alignment["CreationDate"] = "creation_date";
})(Alignment || (Alignment = {}));
/**
 * The repeating period for this mandate
 */
export var PeriodEnum;
(function (PeriodEnum) {
    PeriodEnum["Day"] = "day";
    PeriodEnum["Flexible"] = "flexible";
    PeriodEnum["Month"] = "month";
    PeriodEnum["Week"] = "week";
    PeriodEnum["Year"] = "year";
})(PeriodEnum || (PeriodEnum = {}));
/**
 * This field is optional for mandate requests with any schemes except FasterPayments.
 *
 * This field is required for FasterPayments(VRP) as in the future we plan to support more
 * cases and "vrp_sweeping" is not the most common.
 *
 * Here is what the keys evaluates to:
 * <ul>
 * <li>`bank_debit`: Direct Debit</li>
 * <li>`recurring`: OpenBanking Recurring</li>
 * <li>`vrp_sweeping`: VRP scheme specific sweeping use case</li>
 * </ul>
 */
export var MandateType;
(function (MandateType) {
    MandateType["BankDebit"] = "bank_debit";
    MandateType["Recurring"] = "recurring";
    MandateType["VrpSweeping"] = "vrp_sweeping";
})(MandateType || (MandateType = {}));
/**
 * Verification preference for the mandate. One of:
 * <ul>
 * <li>`minimum`: only verify if absolutely required, such as when part of scheme
 * rules</li>
 * <li>`recommended`: in addition to `minimum`, use the GoCardless payment intelligence
 * solution to decide if a payer should be verified</li>
 * <li>`when_available`: if verification mechanisms are available, use them</li>
 * <li>`always`: as `when_available`, but fail to create the Billing Request if a mechanism
 * isn't available</li>
 * </ul>
 *
 * By default, all Billing Requests use the `recommended` verification preference. It uses
 * GoCardless payment intelligence solution to determine if a payer is fraudulent or not.
 * The verification mechanism is based on the response and the payer may be asked to verify
 * themselves. If the feature is not available, `recommended` behaves like `minimum`.
 *
 * If you never wish to take advantage of our reduced risk products and Verified Mandates as
 * they are released in new schemes, please use the `minimum` verification preference.
 *
 * See [Billing Requests: Creating Verified
 * Mandates](https://developer.gocardless.com/getting-started/billing-requests/verified-mandates/)
 * for more information.
 */
export var MandateRequestVerify;
(function (MandateRequestVerify) {
    MandateRequestVerify["Always"] = "always";
    MandateRequestVerify["Minimum"] = "minimum";
    MandateRequestVerify["Recommended"] = "recommended";
    MandateRequestVerify["WhenAvailable"] = "when_available";
})(MandateRequestVerify || (MandateRequestVerify = {}));
/**
 * This field will decide how GoCardless handles settlement of funds from the customer.
 *
 * - `managed` will be moved through GoCardless' account, batched, and payed out.
 * - `direct` will be a direct transfer from the payer's account to the merchant where
 * invoicing will be handled separately.
 *
 *
 * This field will decide how GoCardless handles settlement of funds from the customer.
 *
 * - `managed` will be moved through GoCardless' account, batched, and payed out.
 * - `direct` will be a direct transfer from the payer's account to the merchant where
 * invoicing will be handled separately.
 *
 * This field will decide how GoCardless handles settlement of funds from the customer.
 * <ul>
 * <li>`managed` will be moved through GoCardless' account, batched, and payed out.</li>
 * <li>`direct` will be a direct transfer from the payer's account to the merchant where
 * invoicing will be handled separately.</li>
 * </ul>
 */
export var FundsSettlement;
(function (FundsSettlement) {
    FundsSettlement["Direct"] = "direct";
    FundsSettlement["Managed"] = "managed";
})(FundsSettlement || (FundsSettlement = {}));
/**
 * Specifies the high-level purpose of a mandate and/or payment using a set of pre-defined
 * categories. Required for the PayTo scheme, optional for all others. Currently `mortgage`,
 * `utility`, `loan`, `dependant_support`, `gambling`, `retail`, `salary`, `personal`,
 * `government`, `pension`, `tax` and `other` are supported.
 *
 * Specifies the high-level purpose of the mandate based on a set of pre-defined categories.
 * PayTo specific
 */
export var PurposeCode;
(function (PurposeCode) {
    PurposeCode["DependantSupport"] = "dependant_support";
    PurposeCode["Gambling"] = "gambling";
    PurposeCode["Government"] = "government";
    PurposeCode["Loan"] = "loan";
    PurposeCode["Mortgage"] = "mortgage";
    PurposeCode["Other"] = "other";
    PurposeCode["Pension"] = "pension";
    PurposeCode["Personal"] = "personal";
    PurposeCode["Retail"] = "retail";
    PurposeCode["Salary"] = "salary";
    PurposeCode["Tax"] = "tax";
    PurposeCode["Utility"] = "utility";
})(PurposeCode || (PurposeCode = {}));
export var AccountType;
(function (AccountType) {
    AccountType["Checking"] = "checking";
    AccountType["Savings"] = "savings";
})(AccountType || (AccountType = {}));
/**
 * One of:
 * <ul>
 * <li>`pending`: the billing request is pending and can be used</li>
 * <li>`ready_to_fulfil`: the billing request is ready to fulfil</li>
 * <li>`fulfilling`: the billing request is currently undergoing fulfilment</li>
 * <li>`fulfilled`: the billing request has been fulfilled and a payment created</li>
 * <li>`cancelled`: the billing request has been cancelled and cannot be used</li>
 * </ul>
 */
export var BillingRequestsStatus;
(function (BillingRequestsStatus) {
    BillingRequestsStatus["Cancelled"] = "cancelled";
    BillingRequestsStatus["Fulfilled"] = "fulfilled";
    BillingRequestsStatus["Fulfilling"] = "fulfilling";
    BillingRequestsStatus["Pending"] = "pending";
    BillingRequestsStatus["ReadyToFulfil"] = "ready_to_fulfil";
})(BillingRequestsStatus || (BillingRequestsStatus = {}));
export var Month;
(function (Month) {
    Month["April"] = "april";
    Month["August"] = "august";
    Month["December"] = "december";
    Month["February"] = "february";
    Month["January"] = "january";
    Month["July"] = "july";
    Month["June"] = "june";
    Month["March"] = "march";
    Month["May"] = "may";
    Month["November"] = "november";
    Month["October"] = "october";
    Month["September"] = "september";
})(Month || (Month = {}));
/**
 * A bank payment scheme. Currently "ach, "autogiro", "bacs", "becs", "becs_nz",
 * "betalingsservice", "faster_payments", "pad", "pay_to", "sepa", "sepa_credit_transfer",
 * "sepa_instant_credit_transfer" are supported.
 *
 * The scheme which this scheme identifier applies to.
 */
export var HomeSchemeEnum;
(function (HomeSchemeEnum) {
    HomeSchemeEnum["Ach"] = "ach";
    HomeSchemeEnum["Autogiro"] = "autogiro";
    HomeSchemeEnum["Bacs"] = "bacs";
    HomeSchemeEnum["Becs"] = "becs";
    HomeSchemeEnum["BecsNz"] = "becs_nz";
    HomeSchemeEnum["Betalingsservice"] = "betalingsservice";
    HomeSchemeEnum["FasterPayments"] = "faster_payments";
    HomeSchemeEnum["Pad"] = "pad";
    HomeSchemeEnum["PayTo"] = "pay_to";
    HomeSchemeEnum["Sepa"] = "sepa";
    HomeSchemeEnum["SepaCreditTransfer"] = "sepa_credit_transfer";
    HomeSchemeEnum["SepaInstantCreditTransfer"] = "sepa_instant_credit_transfer";
})(HomeSchemeEnum || (HomeSchemeEnum = {}));
export var RedirectOrigin;
(function (RedirectOrigin) {
    RedirectOrigin["QrDashboard"] = "QR_dashboard";
    RedirectOrigin["QrDownload"] = "QR_download";
})(RedirectOrigin || (RedirectOrigin = {}));
/**
 * The source of the billing request. Describes how the billing request got created.
 *
 * The source of the payment request. Describes how the payment request got created.
 */
export var BillingRequestSource;
(function (BillingRequestSource) {
    BillingRequestSource["Api"] = "api";
    BillingRequestSource["App"] = "app";
    BillingRequestSource["Dashboard"] = "dashboard";
})(BillingRequestSource || (BillingRequestSource = {}));
/**
 * Currently, can only be `email`.
 */
export var BillingRequestNotifyRequestBodyNotificationType;
(function (BillingRequestNotifyRequestBodyNotificationType) {
    BillingRequestNotifyRequestBodyNotificationType["Email"] = "email";
})(BillingRequestNotifyRequestBodyNotificationType || (BillingRequestNotifyRequestBodyNotificationType = {}));
/**
 * The reason you wish to block this payer, can currently be one of 'identity_fraud',
 * 'no_intent_to_pay', 'unfair_chargeback'. If the reason isn't captured by one of the
 * above then 'other' can be selected but you must provide a reason description.
 */
export var ReasonType;
(function (ReasonType) {
    ReasonType["IdentityFraud"] = "identity_fraud";
    ReasonType["NoIntentToPay"] = "no_intent_to_pay";
    ReasonType["Other"] = "other";
    ReasonType["UnfairChargeback"] = "unfair_chargeback";
})(ReasonType || (ReasonType = {}));
/**
 * Type of entity we will seek to get the associated emails and bank accounts to
 * create blocks from. This can currently be one of 'customer' or 'mandate'.
 *
 * Includes linked resources in the response.
 */
export var ReferenceType;
(function (ReferenceType) {
    ReferenceType["Customer"] = "customer";
    ReferenceType["Mandate"] = "mandate";
})(ReferenceType || (ReferenceType = {}));
/**
 * Type of entity we will seek to match against when blocking the mandate. This
 * can currently be one of 'email', 'email_domain', 'bank_account', or 'bank_name'.
 */
export var BlockType;
(function (BlockType) {
    BlockType["BankAccount"] = "bank_account";
    BlockType["BankName"] = "bank_name";
    BlockType["Email"] = "email";
    BlockType["EmailDomain"] = "email_domain";
})(BlockType || (BlockType = {}));
export var AuddisLive;
(function (AuddisLive) {
    AuddisLive["AuddisLive"] = "auddis-live";
    AuddisLive["NonAuddisLive"] = "non_auddis-live";
})(AuddisLive || (AuddisLive = {}));
export var BulkChangeType;
(function (BulkChangeType) {
    BulkChangeType["Full"] = "full";
    BulkChangeType["Partial"] = "partial";
})(BulkChangeType || (BulkChangeType = {}));
export var PartnerAppIntegration;
(function (PartnerAppIntegration) {
    PartnerAppIntegration["ClubrightPartnerApp"] = "clubright-partner-app";
    PartnerAppIntegration["RecurlyPartnerAppOtherPartnerApp"] = "recurly-partner-app. other-partner-app";
    PartnerAppIntegration["VirtuagymPartnerApp"] = "virtuagym-partner-app";
})(PartnerAppIntegration || (PartnerAppIntegration = {}));
export var PartnerIntegration;
(function (PartnerIntegration) {
    PartnerIntegration["No"] = "no";
    PartnerIntegration["Yes"] = "yes";
})(PartnerIntegration || (PartnerIntegration = {}));
export var TransferType;
(function (TransferType) {
    TransferType["Fm"] = "fm";
    TransferType["Ma"] = "ma";
})(TransferType || (TransferType = {}));
/**
 * Type of the document that has been uploaded
 *
 * List of types of the evidence accepted to challenge the chargeback.
 *
 * List of types of the documents accepted to challenge the chargeback.
 */
export var DocumentType;
(function (DocumentType) {
    DocumentType["AdvanceNotice"] = "ADVANCE_NOTICE";
    DocumentType["OtherDocument"] = "OTHER_DOCUMENT";
    DocumentType["PennyTest"] = "PENNY_TEST";
    DocumentType["ProofOfRefund"] = "PROOF_OF_REFUND";
    DocumentType["SignedContract"] = "SIGNED_CONTRACT";
})(DocumentType || (DocumentType = {}));
export var ChargebackListQueryParamsSort;
(function (ChargebackListQueryParamsSort) {
    ChargebackListQueryParamsSort["ChargeDate"] = "charge_date";
    ChargebackListQueryParamsSort["CreatedAt"] = "created_at";
    ChargebackListQueryParamsSort["ExpectedSettlementDate"] = "expected_settlement_date";
    ChargebackListQueryParamsSort["Id"] = "id";
    ChargebackListQueryParamsSort["SortChargeDate"] = "-charge_date";
    ChargebackListQueryParamsSort["SortCreatedAt"] = "-created_at";
    ChargebackListQueryParamsSort["SortExpectedSettlementDate"] = "-expected_settlement_date";
    ChargebackListQueryParamsSort["SortId"] = "-id";
    ChargebackListQueryParamsSort["SortUpdatedAt"] = "-updated_at";
    ChargebackListQueryParamsSort["UpdatedAt"] = "updated_at";
})(ChargebackListQueryParamsSort || (ChargebackListQueryParamsSort = {}));
/**
 * Status of the chargeback to filter by. _Note:_ `challenged` combines every other status
 * but `created`.
 */
export var StatusFilter;
(function (StatusFilter) {
    StatusFilter["Cancelled"] = "cancelled";
    StatusFilter["Challenged"] = "challenged";
    StatusFilter["Created"] = "created";
    StatusFilter["Processing"] = "processing";
    StatusFilter["Settled"] = "settled";
})(StatusFilter || (StatusFilter = {}));
/**
 * Current status of the chargeback.
 */
export var ChargebackStatus;
(function (ChargebackStatus) {
    ChargebackStatus["Cancelled"] = "cancelled";
    ChargebackStatus["Created"] = "created";
    ChargebackStatus["Processing"] = "processing";
    ChargebackStatus["Settled"] = "settled";
})(ChargebackStatus || (ChargebackStatus = {}));
/**
 * Verification status of the Bank Account. Can be one of `pending`, `in_review` or
 * `successful`
 */
export var CreditorBankAccountsVerificationStatus;
(function (CreditorBankAccountsVerificationStatus) {
    CreditorBankAccountsVerificationStatus["CouldNotVerify"] = "could_not_verify";
    CreditorBankAccountsVerificationStatus["InReview"] = "in_review";
    CreditorBankAccountsVerificationStatus["Pending"] = "pending";
    CreditorBankAccountsVerificationStatus["Successful"] = "successful";
})(CreditorBankAccountsVerificationStatus || (CreditorBankAccountsVerificationStatus = {}));
export var DayOfWeek;
(function (DayOfWeek) {
    DayOfWeek["Friday"] = "friday";
    DayOfWeek["Monday"] = "monday";
    DayOfWeek["Saturday"] = "saturday";
    DayOfWeek["Sunday"] = "sunday";
    DayOfWeek["Thursday"] = "thursday";
    DayOfWeek["Tuesday"] = "tuesday";
    DayOfWeek["Wednesday"] = "wednesday";
})(DayOfWeek || (DayOfWeek = {}));
export var PayoutIntervalUnit;
(function (PayoutIntervalUnit) {
    PayoutIntervalUnit["Daily"] = "daily";
    PayoutIntervalUnit["Monthly"] = "monthly";
    PayoutIntervalUnit["Weekly"] = "weekly";
})(PayoutIntervalUnit || (PayoutIntervalUnit = {}));
/**
 * Permission scope
 */
export var OrganisationDetailsScope;
(function (OrganisationDetailsScope) {
    OrganisationDetailsScope["Admin"] = "admin";
    OrganisationDetailsScope["NoAccess"] = "no_access";
    OrganisationDetailsScope["Null"] = "null";
    OrganisationDetailsScope["ReadOnly"] = "read_only";
    OrganisationDetailsScope["ReadWrite"] = "read_write";
})(OrganisationDetailsScope || (OrganisationDetailsScope = {}));
/**
 * String indicating current refunds status for the creditor. One of:
 * <ul>
 * <li>`not_set`: The creditor's refunds status is not set and refunds are disabled by
 * default.</li>
 * <li>`enabled`: The creditor's refunds are enabled.</li>
 * <li>`disabled`: The creditor's refunds are disabled.</li>
 * </ul>
 */
export var RefundsStatusEnum;
(function (RefundsStatusEnum) {
    RefundsStatusEnum["Disabled"] = "disabled";
    RefundsStatusEnum["Enabled"] = "enabled";
    RefundsStatusEnum["NotSet"] = "not_set";
})(RefundsStatusEnum || (RefundsStatusEnum = {}));
/**
 * The status of the scheme identifier. Only `active` scheme identifiers will be applied to
 * a creditor and used against payments.
 */
export var SchemeIdentifierStatus;
(function (SchemeIdentifierStatus) {
    SchemeIdentifierStatus["Active"] = "active";
    SchemeIdentifierStatus["Pending"] = "pending";
})(SchemeIdentifierStatus || (SchemeIdentifierStatus = {}));
/**
 * The creditor's verification status, indicating whether they can yet receive payouts. For
 * more details on handling verification as a partner, see our ["Helping your users get
 * verified" guide](/getting-started/partners/helping-your-users-get-verified/). One of:
 * <ul>
 * <li>`successful`: The creditor's account is fully verified, and they can receive payouts.
 * Once a creditor has been successfully verified, they may in the future require further
 * verification - for example, if they change their payout bank account, we will have to
 * check that they own the new bank account before they can receive payouts again.</li>
 * <li>`in_review`: The creditor has provided all of the information currently requested,
 * and it is awaiting review by GoCardless before they can be verified and receive
 * payouts.</li>
 * <li>`action_required`: The creditor needs to provide further information to verify their
 * account so they can receive payouts, and should visit the verification flow.</li>
 * </ul>
 */
export var CreditorsVerificationStatus;
(function (CreditorsVerificationStatus) {
    CreditorsVerificationStatus["ActionRequired"] = "action_required";
    CreditorsVerificationStatus["InReview"] = "in_review";
    CreditorsVerificationStatus["Successful"] = "successful";
})(CreditorsVerificationStatus || (CreditorsVerificationStatus = {}));
/**
 * The top up model of the outbound payment account
 * one of:
 * <ul>
 * <li>`direct` - a merchant makes direct bank transfers to fund their payment account </li>
 * <li>`from_collections` - collection payouts are redirected to merchant's payment
 * account</li>
 * </ul>
 */
export var FundingModel;
(function (FundingModel) {
    FundingModel["Direct"] = "direct";
    FundingModel["FromCollections"] = "from_collections";
})(FundingModel || (FundingModel = {}));
export var SchemeFlow;
(function (SchemeFlow) {
    SchemeFlow["Instant"] = "instant";
    SchemeFlow["VrpSweeping"] = "vrp_sweeping";
})(SchemeFlow || (SchemeFlow = {}));
/**
 * Trackable event
 */
export var Event;
(function (Event) {
    Event["BankAuthorisationComplianceRendered"] = "bank_authorisation_compliance_rendered";
})(Event || (Event = {}));
export var CustomerApprovalsResourceType;
(function (CustomerApprovalsResourceType) {
    CustomerApprovalsResourceType["Payment"] = "payment";
    CustomerApprovalsResourceType["Subscription"] = "subscription";
})(CustomerApprovalsResourceType || (CustomerApprovalsResourceType = {}));
export var CustomerApprovalsStatus;
(function (CustomerApprovalsStatus) {
    CustomerApprovalsStatus["Denied"] = "denied";
    CustomerApprovalsStatus["Granted"] = "granted";
    CustomerApprovalsStatus["Pending"] = "pending";
    CustomerApprovalsStatus["Unnecessary"] = "unnecessary";
})(CustomerApprovalsStatus || (CustomerApprovalsStatus = {}));
/**
 * The current verification status of the customer, used to control the verification page on
 * the BRF UI.
 */
export var CustomerEmailVerificationCreateResponseBodyVerificationStatus;
(function (CustomerEmailVerificationCreateResponseBodyVerificationStatus) {
    CustomerEmailVerificationCreateResponseBodyVerificationStatus["Expired"] = "expired";
    CustomerEmailVerificationCreateResponseBodyVerificationStatus["Null"] = "null";
    CustomerEmailVerificationCreateResponseBodyVerificationStatus["Pending"] = "pending";
    CustomerEmailVerificationCreateResponseBodyVerificationStatus["Verified"] = "verified";
})(CustomerEmailVerificationCreateResponseBodyVerificationStatus || (CustomerEmailVerificationCreateResponseBodyVerificationStatus = {}));
/**
 * The direction to sort in.
 * One of:
 * <ul>
 * <li>`asc`</li>
 * <li>`desc`</li>
 * </ul>
 */
export var SortDirection;
(function (SortDirection) {
    SortDirection["Asc"] = "asc";
    SortDirection["Desc"] = "desc";
})(SortDirection || (SortDirection = {}));
/**
 * Field by which to sort records.
 * One of:
 * <ul>
 * <li>`name`</li>
 * <li>`company_name`</li>
 * <li>`created_at`</li>
 * </ul>
 */
export var CustomerListQueryParamsSortField;
(function (CustomerListQueryParamsSortField) {
    CustomerListQueryParamsSortField["CompanyName"] = "company_name";
    CustomerListQueryParamsSortField["CreatedAt"] = "created_at";
    CustomerListQueryParamsSortField["Name"] = "name";
})(CustomerListQueryParamsSortField || (CustomerListQueryParamsSortField = {}));
/**
 * The action that was taken on the notification. Currently this can only be `handled`,
 * which means the integrator sent the notification themselves.
 */
export var ActionTaken;
(function (ActionTaken) {
    ActionTaken["Handled"] = "handled";
})(ActionTaken || (ActionTaken = {}));
/**
 * The type of notification the customer shall receive.
 * One of:
 * <ul>
 * <li>`payment_created`</li>
 * <li>`payment_cancelled`</li>
 * <li>`mandate_created`</li>
 * <li>`mandate_blocked`</li>
 * <li>`subscription_created`</li>
 * <li>`subscription_cancelled`</li>
 * <li>`instalment_schedule_created`</li>
 * <li>`instalment_schedule_cancelled`</li>
 * </ul>
 */
export var CustomerNotificationsType;
(function (CustomerNotificationsType) {
    CustomerNotificationsType["InstalmentScheduleCancelled"] = "instalment_schedule_cancelled";
    CustomerNotificationsType["InstalmentScheduleCreated"] = "instalment_schedule_created";
    CustomerNotificationsType["MandateBlocked"] = "mandate_blocked";
    CustomerNotificationsType["MandateCreated"] = "mandate_created";
    CustomerNotificationsType["PaymentCancelled"] = "payment_cancelled";
    CustomerNotificationsType["PaymentCreated"] = "payment_created";
    CustomerNotificationsType["SubscriptionCancelled"] = "subscription_cancelled";
    CustomerNotificationsType["SubscriptionCreated"] = "subscription_created";
})(CustomerNotificationsType || (CustomerNotificationsType = {}));
export var DashboardNotificationListQueryParamsSort;
(function (DashboardNotificationListQueryParamsSort) {
    DashboardNotificationListQueryParamsSort["CreatedAt"] = "-created_at";
})(DashboardNotificationListQueryParamsSort || (DashboardNotificationListQueryParamsSort = {}));
/**
 * Type of notification
 */
export var DashboardNotificationsNotificationsType;
(function (DashboardNotificationsNotificationsType) {
    DashboardNotificationsNotificationsType["CancelledMandatesAlert"] = "cancelled_mandates_alert";
    DashboardNotificationsNotificationsType["CancelledPaymentsAlert"] = "cancelled_payments_alert";
    DashboardNotificationsNotificationsType["ChargedBackPaymentsAlert"] = "charged_back_payments_alert";
    DashboardNotificationsNotificationsType["FailedPaymentsAlert"] = "failed_payments_alert";
    DashboardNotificationsNotificationsType["MonthlyPayoutSummary"] = "monthly_payout_summary";
    DashboardNotificationsNotificationsType["NewPayout"] = "new_payout";
})(DashboardNotificationsNotificationsType || (DashboardNotificationsNotificationsType = {}));
export var PurpleInclude;
(function (PurpleInclude) {
    PurpleInclude["BillingRequest"] = "billing_request";
    PurpleInclude["Creditor"] = "creditor";
    PurpleInclude["Customer"] = "customer";
    PurpleInclude["InstalmentSchedule"] = "instalment_schedule";
    PurpleInclude["Mandate"] = "mandate";
    PurpleInclude["OutboundPayment"] = "outbound_payment";
    PurpleInclude["PayerAuthorisation"] = "payer_authorisation";
    PurpleInclude["Payment"] = "payment";
    PurpleInclude["Payout"] = "payout";
    PurpleInclude["Refund"] = "refund";
    PurpleInclude["SchemeIdentifier"] = "scheme_identifier";
    PurpleInclude["Subscription"] = "subscription";
    PurpleInclude["SurchargeFee"] = "surcharge_fee";
})(PurpleInclude || (PurpleInclude = {}));
export var FluffyInclude;
(function (FluffyInclude) {
    FluffyInclude["BillingRequest"] = "billing_request";
    FluffyInclude["Creditor"] = "creditor";
    FluffyInclude["InstalmentSchedule"] = "instalment_schedule";
    FluffyInclude["Mandate"] = "mandate";
    FluffyInclude["OutboundPayment"] = "outbound_payment";
    FluffyInclude["PayerAuthorisation"] = "payer_authorisation";
    FluffyInclude["Payment"] = "payment";
    FluffyInclude["Payout"] = "payout";
    FluffyInclude["Refund"] = "refund";
    FluffyInclude["SchemeIdentifier"] = "scheme_identifier";
    FluffyInclude["Subscription"] = "subscription";
})(FluffyInclude || (FluffyInclude = {}));
/**
 * The resource type for this event. One of:
 * <ul>
 * <li>`billing_requests`</li>
 * <li>`creditors`</li>
 * <li>`exports`</li>
 * <li>`instalment_schedules`</li>
 * <li>`mandates`</li>
 * <li>`payer_authorisations`</li>
 * <li>`payments`</li>
 * <li>`payouts`</li>
 * <li>`refunds`</li>
 * <li>`scheme_identifiers`</li>
 * <li>`subscriptions`</li>
 * <li>`outbound_payments`</li>
 * </ul>
 *
 * Type of resource that you'd like to get all events for.
 * Cannot be used together with the `billing_request`, `creditor`,
 * `export`,`instalment_schedule`, `mandate`, `payer_authorisation`, `payment`, `payout`,
 * `refund`, `scheme_identifier`, `subscription` or `outbound_payment` parameters.
 * The type can be one of:
 * <ul>
 * <li>`billing_requests`</li>
 * <li>`creditors`</li>
 * <li>`exports`</li>
 * <li>`instalment_schedules`</li>
 * <li>`mandates`</li>
 * <li>`payer_authorisations`</li>
 * <li>`payments`</li>
 * <li>`payouts`</li>
 * <li>`refunds`</li>
 * <li>`scheme_identifiers`</li>
 * <li>`subscriptions`</li>
 * <li>`outbound_payments`</li>
 * </ul>
 */
export var EventListQueryParamsResourceType;
(function (EventListQueryParamsResourceType) {
    EventListQueryParamsResourceType["BillingRequests"] = "billing_requests";
    EventListQueryParamsResourceType["Creditors"] = "creditors";
    EventListQueryParamsResourceType["Exports"] = "exports";
    EventListQueryParamsResourceType["InstalmentSchedules"] = "instalment_schedules";
    EventListQueryParamsResourceType["Mandates"] = "mandates";
    EventListQueryParamsResourceType["Organisations"] = "organisations";
    EventListQueryParamsResourceType["OutboundPayments"] = "outbound_payments";
    EventListQueryParamsResourceType["PayerAuthorisations"] = "payer_authorisations";
    EventListQueryParamsResourceType["Payments"] = "payments";
    EventListQueryParamsResourceType["Payouts"] = "payouts";
    EventListQueryParamsResourceType["Refunds"] = "refunds";
    EventListQueryParamsResourceType["SchemeIdentifiers"] = "scheme_identifiers";
    EventListQueryParamsResourceType["Subscriptions"] = "subscriptions";
})(EventListQueryParamsResourceType || (EventListQueryParamsResourceType = {}));
/**
 * Who initiated the event. One of:
 * <ul>
 * <li>`bank`: this event was triggered by a report from the banks</li>
 * <li>`gocardless`: this event was performed by GoCardless automatically</li>
 * <li>`api`: this event was triggered by an API endpoint</li>
 * <li>`customer`: this event was triggered by a Customer</li>
 * <li>`payer`: this event was triggered by a Payer</li>
 * </ul>
 */
export var Origin;
(function (Origin) {
    Origin["Api"] = "api";
    Origin["Bank"] = "bank";
    Origin["Customer"] = "customer";
    Origin["Gocardless"] = "gocardless";
    Origin["Payer"] = "payer";
})(Origin || (Origin = {}));
/**
 * A bank payment scheme.
 */
export var DetailsScheme;
(function (DetailsScheme) {
    DetailsScheme["Ach"] = "ach";
    DetailsScheme["Autogiro"] = "autogiro";
    DetailsScheme["Bacs"] = "bacs";
    DetailsScheme["Becs"] = "becs";
    DetailsScheme["BecsNz"] = "becs_nz";
    DetailsScheme["Betalingsservice"] = "betalingsservice";
    DetailsScheme["FasterPayments"] = "faster_payments";
    DetailsScheme["Pad"] = "pad";
    DetailsScheme["PayTo"] = "pay_to";
    DetailsScheme["SepaCor1"] = "sepa_cor1";
    DetailsScheme["SepaCore"] = "sepa_core";
})(DetailsScheme || (DetailsScheme = {}));
/**
 * The type of the event's source.
 */
export var SourceType;
(function (SourceType) {
    SourceType["AccessToken"] = "access_token";
    SourceType["App"] = "app";
    SourceType["GcTeam"] = "gc_team";
    SourceType["User"] = "user";
})(SourceType || (SourceType = {}));
/**
 * One of:
 * <ul>
 * <li>`pending`: we're waiting for GC to create the payments</li>
 * <li>`active`: the payments have been created, and the schedule is active</li>
 * <li>`creation_failed`: payment creation failed</li>
 * <li>`completed`: we have passed the date of the final payment and all payments have been
 * collected</li>
 * <li>`cancelled`: the schedule has been cancelled</li>
 * <li>`errored`: one or more payments have failed</li>
 * </ul>
 *
 * At most five valid status values
 */
export var InstalmentSchedulesStatus;
(function (InstalmentSchedulesStatus) {
    InstalmentSchedulesStatus["Active"] = "active";
    InstalmentSchedulesStatus["Cancelled"] = "cancelled";
    InstalmentSchedulesStatus["Completed"] = "completed";
    InstalmentSchedulesStatus["CreationFailed"] = "creation_failed";
    InstalmentSchedulesStatus["Errored"] = "errored";
    InstalmentSchedulesStatus["Pending"] = "pending";
})(InstalmentSchedulesStatus || (InstalmentSchedulesStatus = {}));
export var ConsentType;
(function (ConsentType) {
    ConsentType["OneOff"] = "one_off";
    ConsentType["Recurring"] = "recurring";
    ConsentType["Single"] = "single";
    ConsentType["Sporadic"] = "sporadic";
    ConsentType["Standing"] = "standing";
})(ConsentType || (ConsentType = {}));
/**
 * One of:
 * <ul>
 * <li>`pending_customer_approval`: the mandate has not yet been signed by the second
 * customer</li>
 * <li>`pending_submission`: the mandate has not yet been submitted to the customer's
 * bank</li>
 * <li>`submitted`: the mandate has been submitted to the customer's bank but has not been
 * processed yet</li>
 * <li>`active`: the mandate has been successfully set up by the customer's bank</li>
 * <li>`suspended_by_payer`: the mandate has been suspended by payer</li>
 * <li>`failed`: the mandate could not be created</li>
 * <li>`cancelled`: the mandate has been cancelled</li>
 * <li>`expired`: the mandate has expired due to dormancy</li>
 * <li>`consumed`: the mandate has been consumed and cannot be reused (note that this only
 * applies to schemes that are per-payment authorised)</li>
 * <li>`blocked`: the mandate has been blocked and payments cannot be created</li>
 * </ul>
 *
 * At most four valid status values
 */
export var MandatesStatus;
(function (MandatesStatus) {
    MandatesStatus["Active"] = "active";
    MandatesStatus["Blocked"] = "blocked";
    MandatesStatus["Cancelled"] = "cancelled";
    MandatesStatus["Consumed"] = "consumed";
    MandatesStatus["Expired"] = "expired";
    MandatesStatus["Failed"] = "failed";
    MandatesStatus["PendingCustomerApproval"] = "pending_customer_approval";
    MandatesStatus["PendingSubmission"] = "pending_submission";
    MandatesStatus["Submitted"] = "submitted";
    MandatesStatus["SuspendedByPayer"] = "suspended_by_payer";
})(MandatesStatus || (MandatesStatus = {}));
/**
 * [ISO 4217](http://en.wikipedia.org/wiki/ISO_4217#Active_codes) currency. Currently only
 * "GBP" is supported.
 *
 * The currency of the Outbound Payments account.
 */
export var OutboundPaymentsCurrency;
(function (OutboundPaymentsCurrency) {
    OutboundPaymentsCurrency["Gbp"] = "GBP";
})(OutboundPaymentsCurrency || (OutboundPaymentsCurrency = {}));
/**
 * One of:
 * <ul>
 * <li>`verifying`: The payment has been
 * [created](#outbound-payments-create-an-outbound-payment) and the verification process has
 * begun.</li>
 * <li>`pending_approval`: The payment is awaiting
 * [approval](#outbound-payments-approve-an-outbound-payment).</li>
 * <li>`scheduled`: The payment has passed verification &
 * [approval](#outbound-payments-approve-an-outbound-payment), but processing has not yet
 * begun.</li>
 * <li>`executing`: The execution date has arrived and the payment has been placed in queue
 * for processing.</li>
 * <li>`executed`: The payment has been accepted by the scheme and is now on its way to the
 * recipient.</li>
 * <li>`cancelled`: The payment has been
 * [cancelled](#outbound-payments-cancel-an-outbound-payment) or was not
 * [approved](#outbound-payments-approve-an-outbound-payment) on time.</li>
 * <li>`failed`: The payment was not sent, usually due to an error while or after
 * executing.</li>
 * </ul>
 */
export var Status;
(function (Status) {
    Status["Cancelled"] = "cancelled";
    Status["Executed"] = "executed";
    Status["Executing"] = "executing";
    Status["Failed"] = "failed";
    Status["PendingApproval"] = "pending_approval";
    Status["Scheduled"] = "scheduled";
    Status["Verifying"] = "verifying";
})(Status || (Status = {}));
/**
 * Result of the verification, could be one of
 * <ul>
 * <li>`full_match`: The verification has confirmed that the account name exactly matches
 * the details provided.</li>
 * <li>`partial_match`:  The verification has confirmed that the account name is similar but
 * does not match to the details provided. </li>
 * <li>`no_match`: The verification concludes the provided name does not match the account
 * details.</li>
 * <li>`unable_to_match`: The verification could not be performed due to recipient bank
 * issues or technical issues </li>
 * </ul>
 */
export var RecipientBankAccountHolderVerificationResult;
(function (RecipientBankAccountHolderVerificationResult) {
    RecipientBankAccountHolderVerificationResult["FullMatch"] = "full_match";
    RecipientBankAccountHolderVerificationResult["NoMatch"] = "no_match";
    RecipientBankAccountHolderVerificationResult["PartialMatch"] = "partial_match";
    RecipientBankAccountHolderVerificationResult["UnableToMatch"] = "unable_to_match";
})(RecipientBankAccountHolderVerificationResult || (RecipientBankAccountHolderVerificationResult = {}));
/**
 * Type of the verification that has been performed
 * eg. [Confirmation of
 * Payee](https://www.wearepay.uk/what-we-do/overlay-services/confirmation-of-payee/)
 */
export var RecipientBankAccountHolderVerificationType;
(function (RecipientBankAccountHolderVerificationType) {
    RecipientBankAccountHolderVerificationType["ConfirmationOfPayee"] = "confirmation_of_payee";
})(RecipientBankAccountHolderVerificationType || (RecipientBankAccountHolderVerificationType = {}));
/**
 * One of:
 * <ul>
 * <li>`created`: The PayerAuthorisation has been created, and not been confirmed yet</li>
 * <li>`submitted`: The payer information has been submitted</li>
 * <li>`confirmed`: PayerAuthorisation is confirmed and resources are ready to be
 * created</li>
 * <li>`completed`: The PayerAuthorisation has been completed and customer, bank_account and
 * mandate has been created</li>
 * <li>`failed`: The PayerAuthorisation has failed and customer, bank_account and mandate is
 * not created</li>
 * </ul>
 */
export var PayerAuthorisationsStatus;
(function (PayerAuthorisationsStatus) {
    PayerAuthorisationsStatus["Completed"] = "completed";
    PayerAuthorisationsStatus["Confirmed"] = "confirmed";
    PayerAuthorisationsStatus["Created"] = "created";
    PayerAuthorisationsStatus["Failed"] = "failed";
    PayerAuthorisationsStatus["Submitted"] = "submitted";
})(PayerAuthorisationsStatus || (PayerAuthorisationsStatus = {}));
/**
 * The source of the payment. Describes how the payment got created.
 */
export var PaymentsSource;
(function (PaymentsSource) {
    PaymentsSource["Api"] = "api";
    PaymentsSource["App"] = "app";
    PaymentsSource["Dashboard"] = "dashboard";
    PaymentsSource["Import"] = "import";
    PaymentsSource["PaymentRequest"] = "payment_request";
})(PaymentsSource || (PaymentsSource = {}));
/**
 * One of:
 * <ul>
 * <li>`pending_customer_approval`: we're waiting for the customer to approve this
 * payment</li>
 * <li>`pending_submission`: the payment has been created, but not yet submitted to the
 * banks</li>
 * <li>`submitted`: the payment has been submitted to the banks</li>
 * <li>`confirmed`: the payment has been confirmed as collected</li>
 * <li>`paid_out`:  the payment has been included in a
 * [payout](#core-endpoints-payouts)</li>
 * <li>`cancelled`: the payment has been cancelled</li>
 * <li>`customer_approval_denied`: the customer has denied approval for the payment. You
 * should contact the customer directly</li>
 * <li>`failed`: the payment failed to be processed. Note that payments can fail after being
 * confirmed if the failure message is sent late by the banks.</li>
 * <li>`charged_back`: the payment has been charged back</li>
 * </ul>
 */
export var PaymentStatusEnum;
(function (PaymentStatusEnum) {
    PaymentStatusEnum["Cancelled"] = "cancelled";
    PaymentStatusEnum["ChargedBack"] = "charged_back";
    PaymentStatusEnum["Confirmed"] = "confirmed";
    PaymentStatusEnum["CustomerApprovalDenied"] = "customer_approval_denied";
    PaymentStatusEnum["Failed"] = "failed";
    PaymentStatusEnum["PaidOut"] = "paid_out";
    PaymentStatusEnum["PendingCustomerApproval"] = "pending_customer_approval";
    PaymentStatusEnum["PendingSubmission"] = "pending_submission";
    PaymentStatusEnum["Submitted"] = "submitted";
})(PaymentStatusEnum || (PaymentStatusEnum = {}));
/**
 * Whether a payout contains merchant revenue or partner fees.
 */
export var PayoutType;
(function (PayoutType) {
    PayoutType["Merchant"] = "merchant";
    PayoutType["Partner"] = "partner";
})(PayoutType || (PayoutType = {}));
/**
 * One of:
 * <ul>
 * <li>`pending`: the payout has been created, but not yet sent to your bank or it is in the
 * process of being exchanged through our FX provider.</li>
 * <li>`paid`: the payout has been sent to the your bank. FX payouts will become `paid`
 * after we emit the `fx_rate_confirmed` webhook.</li>
 * <li>`bounced`: the payout bounced when sent, the payout can be retried.</li>
 * </ul>
 */
export var PayoutsStatus;
(function (PayoutsStatus) {
    PayoutsStatus["Bounced"] = "bounced";
    PayoutsStatus["Paid"] = "paid";
    PayoutsStatus["Pending"] = "pending";
})(PayoutsStatus || (PayoutsStatus = {}));
/**
 * One of:
 * <ul>
 * <li>`created`: the refund has been created</li>
 * <li>`pending_submission`: the refund has been created, but not yet submitted to the
 * banks</li>
 * <li>`submitted`: the refund has been submitted to the banks</li>
 * <li>`paid`:  the refund has been included in a [payout](#core-endpoints-payouts)</li>
 * <li>`cancelled`: the refund has been cancelled</li>
 * <li>`bounced`: the refund has failed to be paid</li>
 * <li>`funds_returned`: the refund has had its funds returned</li>
 * </ul>
 */
export var RefundsStatus;
(function (RefundsStatus) {
    RefundsStatus["Bounced"] = "bounced";
    RefundsStatus["Cancelled"] = "cancelled";
    RefundsStatus["Created"] = "created";
    RefundsStatus["FundsReturned"] = "funds_returned";
    RefundsStatus["Paid"] = "paid";
    RefundsStatus["PendingSubmission"] = "pending_submission";
    RefundsStatus["Submitted"] = "submitted";
})(RefundsStatus || (RefundsStatus = {}));
/**
 * One of:
 * <ul>
 * <li> `fm`: GoCardless owns and manages the scheme identifier.
 * Payments are made into our bank account </li>
 * <li> `ma`: You own the scheme identifier and manage it.
 * You've given access to your reports so GoCardless can process them.
 * Payments are made into your bank account </li>
 * <li> `gc`: Same as `fm`, but you use our shared scheme identifier </li>
 * </ul>
 */
export var ManagementType;
(function (ManagementType) {
    ManagementType["Fm"] = "fm";
    ManagementType["Gc"] = "gc";
    ManagementType["Ma"] = "ma";
})(ManagementType || (ManagementType = {}));
/**
 * One of:
 * <ul>
 * <li>`pending_customer_approval`: the subscription is waiting for customer approval before
 * becoming active</li>
 * <li>`customer_approval_denied`: the customer did not approve the subscription</li>
 * <li>`active`: the subscription is currently active and will continue to create
 * payments</li>
 * <li>`finished`: all of the payments scheduled for creation under this subscription have
 * been created</li>
 * <li>`cancelled`: the subscription has been cancelled and will no longer create
 * payments</li>
 * <li>`paused`: the subscription has been paused and will not create payments</li>
 * </ul>
 *
 * Upto 5 of:
 * <ul>
 * <li>`pending_customer_approval`</li>
 * <li>`customer_approval_denied`</li>
 * <li>`active`</li>
 * <li>`finished`</li>
 * <li>`cancelled`</li>
 * <li>`paused`</li>
 * </ul>
 * Omit entirely to include subscriptions in all states.
 */
export var SubscriptionsStatus;
(function (SubscriptionsStatus) {
    SubscriptionsStatus["Active"] = "active";
    SubscriptionsStatus["Cancelled"] = "cancelled";
    SubscriptionsStatus["CustomerApprovalDenied"] = "customer_approval_denied";
    SubscriptionsStatus["Finished"] = "finished";
    SubscriptionsStatus["Paused"] = "paused";
    SubscriptionsStatus["PendingCustomerApproval"] = "pending_customer_approval";
})(SubscriptionsStatus || (SubscriptionsStatus = {}));
/**
 * The type of resource specified in the resource_id
 */
export var ResourceEnum;
(function (ResourceEnum) {
    ResourceEnum["BillingRequest"] = "billing_request";
    ResourceEnum["Customer"] = "customer";
    ResourceEnum["Mandate"] = "mandate";
    ResourceEnum["Payment"] = "payment";
})(ResourceEnum || (ResourceEnum = {}));
/**
 * The type of the export
 */
export var ExportType;
(function (ExportType) {
    ExportType["AuthorisationRequests"] = "authorisation_requests";
    ExportType["CustomerBankAccounts"] = "customer_bank_accounts";
    ExportType["CustomersIndex"] = "customers_index";
    ExportType["EventsIndex"] = "events_index";
    ExportType["GcInvalidAuthorisationRequests"] = "gc_invalid_authorisation_requests";
    ExportType["MandateEvents"] = "mandate_events";
    ExportType["OrganisationAuthorisations"] = "organisation_authorisations";
    ExportType["PartnerFees"] = "partner_fees";
    ExportType["PaymentAccountStatement"] = "payment_account_statement";
    ExportType["PaymentEvents"] = "payment_events";
    ExportType["PaymentsImportTemplate"] = "payments_import_template";
    ExportType["PaymentsIndex"] = "payments_index";
    ExportType["PayoutEvents"] = "payout_events";
    ExportType["PayoutEventsBreakdown"] = "payout_events_breakdown";
    ExportType["PayoutEventsReconciliation"] = "payout_events_reconciliation";
    ExportType["PayoutTransactionsBreakdown"] = "payout_transactions_breakdown";
    ExportType["PayoutTransactionsReconciliation"] = "payout_transactions_reconciliation";
    ExportType["PayoutsIndex"] = "payouts_index";
    ExportType["RefundEvents"] = "refund_events";
    ExportType["RefundsIndex"] = "refunds_index";
    ExportType["SubscriptionEvents"] = "subscription_events";
    ExportType["SubscriptionsIndex"] = "subscriptions_index";
    ExportType["Users"] = "users";
})(ExportType || (ExportType = {}));
/**
 * The current status of the export
 */
export var ExportsStatus;
(function (ExportsStatus) {
    ExportsStatus["Pending"] = "pending";
    ExportsStatus["Processing"] = "processing";
    ExportsStatus["Successful"] = "successful";
    ExportsStatus["Unsuccessful"] = "unsuccessful";
})(ExportsStatus || (ExportsStatus = {}));
/**
 * The type of pricing
 */
export var PricingType;
(function (PricingType) {
    PricingType["Domestic"] = "domestic";
    PricingType["International"] = "international";
    PricingType["SchemeGroup"] = "scheme_group";
})(PricingType || (PricingType = {}));
/**
 * The type of discount.
 */
export var DiscountType;
(function (DiscountType) {
    DiscountType["ActivationDiscount90_Days"] = "activation_discount_90_days";
    DiscountType["Charity"] = "charity";
    DiscountType["CustomerSupport30_DaysDiscount"] = "customer_support_30_days_discount";
    DiscountType["CustomisableActivationDiscount"] = "customisable_activation_discount";
    DiscountType["FederationOfSmallBusinessMemberDiscount"] = "federation_of_small_business_member_discount";
    DiscountType["FirstPayoutDiscount30_DaysTgExp"] = "first_payout_discount_30_days_tg_exp";
    DiscountType["PreActiveDiscount90_Days"] = "pre_active_discount_90_days";
    DiscountType["The10DaysFreeFeeActivationUploadDocument"] = "10days_free_fee_activation_upload_document";
    DiscountType["The15DaysFreeFeeActivationCompleteSetup"] = "15days_free_fee_activation_complete_setup";
    DiscountType["The15DaysFreeFeeActivationCompleteSetupExtension"] = "15days_free_fee_activation_complete_setup_extension";
    DiscountType["The20DaysFreeFeeActivation"] = "20days_free_fee_activation";
    DiscountType["The30DaysFreeFeeActivationAddCustomerOrPayment"] = "30days_free_fee_activation_add_customer_or_payment";
    DiscountType["The30DaysFreeFeeActivationAddFirstCustomer"] = "30days_free_fee_activation_add_first_customer";
    DiscountType["The30DaysFreeFeeActivationCollectFirstPayment"] = "30days_free_fee_activation_collect_first_payment";
    DiscountType["The30DaysFreeFeeActivationCompleteSetup"] = "30days_free_fee_activation_complete_setup";
    DiscountType["The30DaysFreeFeeActivationCreateAccount"] = "30days_free_fee_activation_create_account";
    DiscountType["The40DaysFreeFeeActivationCompleteSetup"] = "40days_free_fee_activation_complete_setup";
    DiscountType["The60DaysFreeFeeActivationAddCustomerOrPayment"] = "60days_free_fee_activation_add_customer_or_payment";
    DiscountType["The90DaysFreeFeeActivation"] = "90days_free_fee_activation";
    DiscountType["Vat"] = "vat";
})(DiscountType || (DiscountType = {}));
/**
 * The current status of the import
 */
export var ImportsStatus;
(function (ImportsStatus) {
    ImportsStatus["ApprovalRequested"] = "approval_requested";
    ImportsStatus["Cancelled"] = "cancelled";
    ImportsStatus["Created"] = "created";
    ImportsStatus["Invalid"] = "invalid";
    ImportsStatus["Processed"] = "processed";
    ImportsStatus["Processing"] = "processing";
    ImportsStatus["Valid"] = "valid";
    ImportsStatus["Validating"] = "validating";
})(ImportsStatus || (ImportsStatus = {}));
/**
 * The type of the import
 */
export var ImportsType;
(function (ImportsType) {
    ImportsType["BulkChangeAch"] = "bulk_change_ach";
    ImportsType["BulkChangeAutogiro"] = "bulk_change_autogiro";
    ImportsType["BulkChangeBacs"] = "bulk_change_bacs";
    ImportsType["BulkChangeBecs"] = "bulk_change_becs";
    ImportsType["BulkChangeBecsNz"] = "bulk_change_becs_nz";
    ImportsType["BulkChangeBetalingsservice"] = "bulk_change_betalingsservice";
    ImportsType["BulkChangePad"] = "bulk_change_pad";
    ImportsType["BulkChangeSepa"] = "bulk_change_sepa";
    ImportsType["BulkChangeSepaKeepReference"] = "bulk_change_sepa_keep_reference";
    ImportsType["BulkChangeSepaOwnCid"] = "bulk_change_sepa_own_cid";
    ImportsType["BulkChangeSepaOwnCidKeepReference"] = "bulk_change_sepa_own_cid_keep_reference";
    ImportsType["Customer"] = "customer";
    ImportsType["MandateMigrationPayTo"] = "mandate_migration_pay_to";
    ImportsType["Payment"] = "payment";
})(ImportsType || (ImportsType = {}));
/**
 * Field by which to sort records.
 */
export var ImportEntryListQueryParamsSortField;
(function (ImportEntryListQueryParamsSortField) {
    ImportEntryListQueryParamsSortField["FileRowNumber"] = "file_row_number";
})(ImportEntryListQueryParamsSortField || (ImportEntryListQueryParamsSortField = {}));
/**
 * The current status of the import entry
 */
export var ImportEntryListQueryParamsStatus;
(function (ImportEntryListQueryParamsStatus) {
    ImportEntryListQueryParamsStatus["Invalid"] = "invalid";
    ImportEntryListQueryParamsStatus["Processed"] = "processed";
    ImportEntryListQueryParamsStatus["SuccessfullyProcessed"] = "successfully_processed";
    ImportEntryListQueryParamsStatus["UnsuccessfullyProcessed"] = "unsuccessfully_processed";
    ImportEntryListQueryParamsStatus["Valid"] = "valid";
})(ImportEntryListQueryParamsStatus || (ImportEntryListQueryParamsStatus = {}));
/**
 * Includes linked resources in the response. It must be set to `customer`.
 */
export var InstalmentScheduleListQueryParamsInclude;
(function (InstalmentScheduleListQueryParamsInclude) {
    InstalmentScheduleListQueryParamsInclude["Customer"] = "customer";
})(InstalmentScheduleListQueryParamsInclude || (InstalmentScheduleListQueryParamsInclude = {}));
/**
 * The status of the institution
 */
export var InstitutionStatus;
(function (InstitutionStatus) {
    InstitutionStatus["Disabled"] = "disabled";
    InstitutionStatus["Enabled"] = "enabled";
    InstitutionStatus["TemporarilyDisabled"] = "temporarily_disabled";
})(InstitutionStatus || (InstitutionStatus = {}));
/**
 * The status of the mandate import.
 * <ul>
 * <li>`created`: A new mandate import.</li>
 * <li>`submitted`: After the integrator has finished adding mandates and <a
 * href="#mandate-imports-submit-a-mandate-import">submitted</a> the import.</li>
 * <li>`cancelled`: If the integrator <a
 * href="#mandate-imports-cancel-a-mandate-import">cancelled</a> the mandate import.</li>
 * <li>`processing`: Once a mandate import has been approved by a GoCardless team member it
 * will be in this state while mandates are imported.</li>
 * <li>`processed`: When all mandates have been imported successfully.</li>
 * </ul>
 */
export var MandateImportsStatus;
(function (MandateImportsStatus) {
    MandateImportsStatus["Cancelled"] = "cancelled";
    MandateImportsStatus["Created"] = "created";
    MandateImportsStatus["Processed"] = "processed";
    MandateImportsStatus["Processing"] = "processing";
    MandateImportsStatus["Submitted"] = "submitted";
})(MandateImportsStatus || (MandateImportsStatus = {}));
/**
 * One of:
 * <ul>
 * <li>`sucessfully_processed`: the entry has been imported and the associated records
 * created.</li>
 * <li>`unsuccessfully_processed`: the entry could not be processed due to an error, see the
 * 'processing_errors' value</li>
 * </ul>
 */
export var MandateImportEntryListQueryParamsStatus;
(function (MandateImportEntryListQueryParamsStatus) {
    MandateImportEntryListQueryParamsStatus["SuccessfullyProcessed"] = "successfully_processed";
    MandateImportEntryListQueryParamsStatus["UnsuccessfullyProcessed"] = "unsuccessfully_processed";
})(MandateImportEntryListQueryParamsStatus || (MandateImportEntryListQueryParamsStatus = {}));
/**
 * Status of the mandate migration.
 */
export var MandateMigrationsStatus;
(function (MandateMigrationsStatus) {
    MandateMigrationsStatus["Created"] = "created";
    MandateMigrationsStatus["Migrated"] = "migrated";
    MandateMigrationsStatus["Migrating"] = "migrating";
    MandateMigrationsStatus["NotEligible"] = "not_eligible";
    MandateMigrationsStatus["OptedOut"] = "opted_out";
    MandateMigrationsStatus["Scheduled"] = "scheduled";
})(MandateMigrationsStatus || (MandateMigrationsStatus = {}));
/**
 * How to sort the results
 */
export var SortMode;
(function (SortMode) {
    SortMode["AccessCount"] = "access_count";
    SortMode["Default"] = "default";
})(SortMode || (SortMode = {}));
/**
 * Type of get started journey for the first payment journey experiment.
 */
export var FpjGetStartedType;
(function (FpjGetStartedType) {
    FpjGetStartedType["Api"] = "api";
    FpjGetStartedType["App"] = "app";
    FpjGetStartedType["Dashboard"] = "dashboard";
    FpjGetStartedType["Null"] = "null";
    FpjGetStartedType["Skip"] = "skip";
})(FpjGetStartedType || (FpjGetStartedType = {}));
/**
 * Label assigned to merchant
 */
export var Category;
(function (Category) {
    Category["ApprovedEducationMerchant"] = "approved_education_merchant";
    Category["ApprovedIndustry"] = "approved_industry";
    Category["Other"] = "other";
})(Category || (Category = {}));
/**
 * Includes linked resources in the response.
 */
export var TentacledInclude;
(function (TentacledInclude) {
    TentacledInclude["Customer"] = "customer";
    TentacledInclude["RecipientBankAccount"] = "recipient_bank_account";
})(TentacledInclude || (TentacledInclude = {}));
/**
 * Field by which to sort records.
 * One of:
 * <ul>
 * <li>`execution_date`</li>
 * <li>`created_at`</li>
 * </ul>
 */
export var OutboundPaymentListQueryParamsSortField;
(function (OutboundPaymentListQueryParamsSortField) {
    OutboundPaymentListQueryParamsSortField["CreatedAt"] = "created_at";
    OutboundPaymentListQueryParamsSortField["ExecutionDate"] = "execution_date";
})(OutboundPaymentListQueryParamsSortField || (OutboundPaymentListQueryParamsSortField = {}));
/**
 * The type of partner fee
 */
export var PartnerFeeListQueryParamsType;
(function (PartnerFeeListQueryParamsType) {
    PartnerFeeListQueryParamsType["App"] = "app";
    PartnerFeeListQueryParamsType["RevenueShare"] = "revenue_share";
})(PartnerFeeListQueryParamsType || (PartnerFeeListQueryParamsType = {}));
/**
 * One of:
 * <ul>
 * <li>`enabled`: the paylink is enabled and can be used</li>
 * <li>`disabled`: the paylink has been disabled and cannot be used</li>
 * </ul>
 *
 * One of:
 * - `enabled`: the remember customer detail record is enabled
 * - `disabled`: the remember customer detail record is disabled
 */
export var PaylinksStatus;
(function (PaylinksStatus) {
    PaylinksStatus["Disabled"] = "disabled";
    PaylinksStatus["Enabled"] = "enabled";
})(PaylinksStatus || (PaylinksStatus = {}));
/**
 * The maximum probability of failure which is allowed for retrying a payment.
 */
export var MaxFailureProbability;
(function (MaxFailureProbability) {
    MaxFailureProbability["The090"] = "0.90";
    MaxFailureProbability["The100"] = "1.00";
})(MaxFailureProbability || (MaxFailureProbability = {}));
/**
 * The type of limit
 */
export var LimitType;
(function (LimitType) {
    LimitType["Maximum"] = "maximum";
    LimitType["Minimum"] = "minimum";
})(LimitType || (LimitType = {}));
/**
 * One of:
 * <ul>
 * <li>`subscription`: the payment was created as part of a subscription</li>
 * <li>`instalment_schedule`: the payment was created as part of an instalment schedule</li>
 * <li>`one_off`: the payment was created as a one-off payment</li>
 * </ul>
 */
export var PaymentType;
(function (PaymentType) {
    PaymentType["InstalmentSchedule"] = "instalment_schedule";
    PaymentType["OneOff"] = "one_off";
    PaymentType["Subscription"] = "subscription";
})(PaymentType || (PaymentType = {}));
/**
 * Field by which to sort records.
 * One of:
 * <ul>
 * <li>`charge_date`</li>
 * <li>`amount`</li>
 * </ul>
 *
 * Field by which to sort records.
 * One of:
 * <ul>
 * <li>`name`</li>
 * <li>`company_name`</li>
 * <li>`created_at`</li>
 * </ul>
 */
export var PaymentListQueryParamsSortField;
(function (PaymentListQueryParamsSortField) {
    PaymentListQueryParamsSortField["Amount"] = "amount";
    PaymentListQueryParamsSortField["ChargeDate"] = "charge_date";
    PaymentListQueryParamsSortField["FailureProbability"] = "failure_probability";
})(PaymentListQueryParamsSortField || (PaymentListQueryParamsSortField = {}));
/**
 * One of:
 * <ul>
 * <li>`pending`: the payment_request is pending and can be used</li>
 * <li>`fulfilled`: the payment_request has been fulfilled and a payment created</li>
 * </ul>
 */
export var PaymentRequestListQueryParamsStatus;
(function (PaymentRequestListQueryParamsStatus) {
    PaymentRequestListQueryParamsStatus["Fulfilled"] = "fulfilled";
    PaymentRequestListQueryParamsStatus["Pending"] = "pending";
})(PaymentRequestListQueryParamsStatus || (PaymentRequestListQueryParamsStatus = {}));
/**
 * The type of the credit (positive) or debit (negative) item in the payout (inclusive of
 * VAT if applicable). One of:
 * <ul>
 * <li>`payment_paid_out` (credit)</li>
 * <li>`payment_failed` (debit): The payment failed to be processed.</li>
 * <li>`payment_charged_back` (debit): The payment has been charged back.</li>
 * <li>`payment_refunded` (debit): The payment has been refunded to the customer.</li>
 * <li>`refund` (debit): A refund sent to a customer, not linked to a payment.</li>
 * <li>`refund_funds_returned` (credit): The refund could not be sent to the customer, and
 * the funds have been returned to you.</li>
 * <li>`gocardless_fee` (credit/debit): The fees that GoCardless charged for a payment. In
 * the case of a payment failure or chargeback, these will appear as credits. Will include
 * taxes if applicable for merchants.</li>
 * <li>`app_fee` (credit/debit): The optional fees that a partner may have taken for a
 * payment. In the case of a payment failure or chargeback, these will appear as
 * credits.</li>
 * <li>`revenue_share` (credit/debit): A share of the fees that GoCardless collected which
 * some partner integrations receive when their users take payments. Only shown in partner
 * payouts. In the case of a payment failure or chargeback, these will appear as
 * credits.</li>
 * <li>`surcharge_fee` (credit/debit): GoCardless deducted a surcharge fee as the payment
 * failed or was charged back, or refunded a surcharge fee as the bank or customer cancelled
 * the chargeback. Will include taxes if applicable for merchants.</li>
 * </ul>
 */
export var PayoutItemType;
(function (PayoutItemType) {
    PayoutItemType["AppFee"] = "app_fee";
    PayoutItemType["GocardlessFee"] = "gocardless_fee";
    PayoutItemType["PaymentChargedBack"] = "payment_charged_back";
    PayoutItemType["PaymentFailed"] = "payment_failed";
    PayoutItemType["PaymentPaidOut"] = "payment_paid_out";
    PayoutItemType["PaymentRefunded"] = "payment_refunded";
    PayoutItemType["Refund"] = "refund";
    PayoutItemType["RefundFundsReturned"] = "refund_funds_returned";
    PayoutItemType["RevenueShare"] = "revenue_share";
    PayoutItemType["SurchargeFee"] = "surcharge_fee";
})(PayoutItemType || (PayoutItemType = {}));
/**
 * The current status of the penny test.
 */
export var PennyTestsStatus;
(function (PennyTestsStatus) {
    PennyTestsStatus["Approved"] = "approved";
    PennyTestsStatus["Cancelled"] = "cancelled";
    PennyTestsStatus["Failed"] = "failed";
    PennyTestsStatus["Pending"] = "pending";
})(PennyTestsStatus || (PennyTestsStatus = {}));
/**
 * One of:
 * <ul>
 * <li>`active`: the plan is active and subscriptions can be created for it</li>
 * <li>`paused`: the plan and all of its active subscriptions have been paused and will not
 * create payments.  The paused subscriptions will resume when the plan does.</li>
 * <li>`cancelled`: the plan has been cancelled and subscriptions can no longer be created
 * for it. There are no active subscriptions for this plan.</li>
 * </ul>
 */
export var PlansStatus;
(function (PlansStatus) {
    PlansStatus["Active"] = "active";
    PlansStatus["Cancelled"] = "cancelled";
    PlansStatus["Paused"] = "paused";
})(PlansStatus || (PlansStatus = {}));
/**
 * One of:
 * <ul>
 * <li>`pending`: the pause_date is in the future, and the plan has not yet been paused</li>
 * <li>`paused`: the pause_date is in the past, and the plan is currently paused</li>
 * <li>`resumed`: the resume_date is in the past, and the plan has been unpaused</li>
 * <li>`cancelled`: this was cancelled before the pause began</li>
 * </ul>
 */
export var PlanSchedulersStatus;
(function (PlanSchedulersStatus) {
    PlanSchedulersStatus["Cancelled"] = "cancelled";
    PlanSchedulersStatus["Paused"] = "paused";
    PlanSchedulersStatus["Pending"] = "pending";
    PlanSchedulersStatus["Resumed"] = "resumed";
})(PlanSchedulersStatus || (PlanSchedulersStatus = {}));
/**
 * Union of access scopes, defining the policy for associated session tokens.
 *
 * Scopes are combined together in union. Each scope is associated with a typical frontend
 * flow.
 *
 * <ul>
 * <li>`payment_requests`: Creation of Payment Requests, and endpoints that help to build
 * Javascript flows.</li>
 * <li>`ui_components`: Permits access to Billing Request endpoints required by our UI
 * Components library.</li>
 * </ul>
 */
export var PublicTokenCreateRequestBodyScope;
(function (PublicTokenCreateRequestBodyScope) {
    PublicTokenCreateRequestBodyScope["PaymentRequests"] = "payment_requests";
    PublicTokenCreateRequestBodyScope["UiComponents"] = "ui_components";
})(PublicTokenCreateRequestBodyScope || (PublicTokenCreateRequestBodyScope = {}));
/**
 * The notification type.
 */
export var RateLimitedNotificationCreateRequestBodyNotificationType;
(function (RateLimitedNotificationCreateRequestBodyNotificationType) {
    RateLimitedNotificationCreateRequestBodyNotificationType["AuthorisationRequest"] = "authorisation_request";
    RateLimitedNotificationCreateRequestBodyNotificationType["DualSignature"] = "dual_signature";
    RateLimitedNotificationCreateRequestBodyNotificationType["PaymentApproval"] = "payment_approval";
    RateLimitedNotificationCreateRequestBodyNotificationType["SubscriptionApproval"] = "subscription_approval";
})(RateLimitedNotificationCreateRequestBodyNotificationType || (RateLimitedNotificationCreateRequestBodyNotificationType = {}));
/**
 * The type of resource the notification belongs to.
 */
export var RateLimitedNotificationCreateRequestBodyResourceType;
(function (RateLimitedNotificationCreateRequestBodyResourceType) {
    RateLimitedNotificationCreateRequestBodyResourceType["AuthorisationRequest"] = "authorisation_request";
    RateLimitedNotificationCreateRequestBodyResourceType["Mandate"] = "mandate";
    RateLimitedNotificationCreateRequestBodyResourceType["Payment"] = "payment";
    RateLimitedNotificationCreateRequestBodyResourceType["Subscription"] = "subscription";
})(RateLimitedNotificationCreateRequestBodyResourceType || (RateLimitedNotificationCreateRequestBodyResourceType = {}));
/**
 * Flag to check if the user should follow the captcha or email verification flow.
 */
export var VerificationMethod;
(function (VerificationMethod) {
    VerificationMethod["CaptchaVerification"] = "captcha_verification";
    VerificationMethod["EmailVerification"] = "email_verification";
})(VerificationMethod || (VerificationMethod = {}));
/**
 * The type of the document (id, passport, etc.)
 */
export var UploadedDocumentType;
(function (UploadedDocumentType) {
    UploadedDocumentType["AmendedFoundingStatement"] = "amended_founding_statement";
    UploadedDocumentType["BankStatement"] = "bank_statement";
    UploadedDocumentType["BeneficialOwnersDocument"] = "beneficial_owners_document";
    UploadedDocumentType["BoardMeetingMinutes"] = "board_meeting_minutes";
    UploadedDocumentType["BusinessOwnersDocument"] = "business_owners_document";
    UploadedDocumentType["CertificateOfIncorporation"] = "certificate_of_incorporation";
    UploadedDocumentType["CertifiedCopyOfThePartnershipAgreement"] = "certified_copy_of_the_partnership_agreement";
    UploadedDocumentType["CertifiedListOfDirectors"] = "certified_list_of_directors";
    UploadedDocumentType["CertifiedListOfUbos"] = "certified_list_of_ubos";
    UploadedDocumentType["CharityOverviewFromTheCharitiesRegulator"] = "charity_overview_from_the_charities_regulator";
    UploadedDocumentType["CompanyAnnualReturn"] = "company_annual_return";
    UploadedDocumentType["CompanyAnnualReturnCro"] = "company_annual_return_cro";
    UploadedDocumentType["CompanyArsredovisning"] = "company_arsredovisning";
    UploadedDocumentType["CompanyArticlesOfAssociation"] = "company_articles_of_association";
    UploadedDocumentType["CompanyArticlesOfAssociationRegitreCommerce"] = "company_articles_of_association_regitre_commerce";
    UploadedDocumentType["CompanyCantonRegistryEntry"] = "company_canton_registry_entry";
    UploadedDocumentType["CompanyCertificateOfShareCapital"] = "company_certificate_of_share_capital";
    UploadedDocumentType["CompanyConstitutionCbe"] = "company_constitution_cbe";
    UploadedDocumentType["CompanyDeedOfIncorporationMercantilRegister"] = "company_deed_of_incorporation_mercantil_register";
    UploadedDocumentType["CompanyEntryAjpesRegister"] = "company_entry_ajpes_register";
    UploadedDocumentType["CompanyEntryAtRegistry"] = "company_entry_at_registry";
    UploadedDocumentType["CompanyEntryCentralBusinessRegisterDk"] = "company_entry_central_business_register_dk";
    UploadedDocumentType["CompanyEntryNzbn"] = "company_entry_nzbn";
    UploadedDocumentType["CompanyEntryOnStateRegistry"] = "company_entry_on_state_registry";
    UploadedDocumentType["CompanyEntryRegistrarOfCompanies"] = "company_entry_registrar_of_companies";
    UploadedDocumentType["CompanyEntryVerejnyRejstrik"] = "company_entry_verejny_rejstrik";
    UploadedDocumentType["CompanyExtractAsic"] = "company_extract_asic";
    UploadedDocumentType["CompanyKbisExtract"] = "company_kbis_extract";
    UploadedDocumentType["CompanyMemorandumArticlesOfAssociation"] = "company_memorandum_articles_of_association";
    UploadedDocumentType["CompanyMemorandumOfAssociationBolagsverket"] = "company_memorandum_of_association_bolagsverket";
    UploadedDocumentType["CompanyMfsaEntry"] = "company_mfsa_entry";
    UploadedDocumentType["CompanyNtroRegistration"] = "company_ntro_registration";
    UploadedDocumentType["CompanyObchodnyRegisterEntry"] = "company_obchodny_register_entry";
    UploadedDocumentType["CompanyRegisterOfNpleEntry"] = "company_register_of_nple_entry";
    UploadedDocumentType["CompanyRegisterStatutoryCertificate"] = "company_register_statutory_certificate";
    UploadedDocumentType["CompanyRegistrationBronnoysundregistrene"] = "company_registration_bronnoysundregistrene";
    UploadedDocumentType["CompanyRegistrationEuropeanJusticePortal"] = "company_registration_european_justice_portal";
    UploadedDocumentType["CompanyRegistrationKvk"] = "company_registration_kvk";
    UploadedDocumentType["CompanyRegistrationTradeRegister"] = "company_registration_trade_register";
    UploadedDocumentType["CompanyRegistrationUnternehmensregister"] = "company_registration_unternehmensregister";
    UploadedDocumentType["CompanyReportRegistroDelleImprese"] = "company_report_registro_delle_imprese";
    UploadedDocumentType["CompanySuskiRegistarEntry"] = "company_suski_registar_entry";
    UploadedDocumentType["CompanyZefixCantonRegistryEntry"] = "company_zefix_canton_registry_entry";
    UploadedDocumentType["CouncilTaxBill"] = "council_tax_bill";
    UploadedDocumentType["CurrentPrintoutFromTheHandelsregister"] = "current_printout_from_the_handelsregister";
    UploadedDocumentType["DrivingLicence"] = "driving_licence";
    UploadedDocumentType["EntryOnTheProvincialOrTerritorialRegister"] = "entry_on_the_provincial_or_territorial_register";
    UploadedDocumentType["ExtractFromJournalOfficiel"] = "extract_from_journal_officiel";
    UploadedDocumentType["FinancialStatement"] = "financial_statement";
    UploadedDocumentType["FoundingStatement"] = "founding_statement";
    UploadedDocumentType["GreenBarCodedIdentityBook"] = "green_bar_coded_identity_book";
    UploadedDocumentType["IncorporationDocuments"] = "incorporation_documents";
    UploadedDocumentType["KrsRegistrationEntry"] = "krs_registration_entry";
    UploadedDocumentType["MinutesFromLastGeneralMeeting"] = "minutes_from_last_general_meeting";
    UploadedDocumentType["NationalIdentityCard"] = "national_identity_card";
    UploadedDocumentType["OperatingAgreement"] = "operating_agreement";
    UploadedDocumentType["PartnershipEntryOnAProfessionalAssociationMembershipDirectory"] = "partnership_entry_on_a_professional_association_membership_directory";
    UploadedDocumentType["Passport"] = "passport";
    UploadedDocumentType["RecentAnnualReport"] = "recent_annual_report";
    UploadedDocumentType["RecentFilingFromStateRegistry"] = "recent_filing_from_state_registry";
    UploadedDocumentType["RegistarOfCompaniesOfficialReceiverEntry"] = "registar_of_companies_official_receiver_entry";
    UploadedDocumentType["RegistrarOfCompaniesAndCloseCorporationsCertificateOfConfirmation"] = "registrar_of_companies_and_close_corporations_certificate_of_confirmation";
    UploadedDocumentType["ResidencePermit"] = "residence_permit";
    UploadedDocumentType["ShareholderAgreement"] = "shareholder_agreement";
    UploadedDocumentType["ShareholderMeetingReport"] = "shareholder_meeting_report";
    UploadedDocumentType["SituationAuRepertoireSiren"] = "situation_au_repertoire_siren";
    UploadedDocumentType["TrustDeed"] = "trust_deed";
    UploadedDocumentType["TvLicence"] = "tv_licence";
    UploadedDocumentType["Unknown"] = "unknown";
    UploadedDocumentType["UptodateCompanyArticlesOfAssociation"] = "uptodate_company_articles_of_association";
    UploadedDocumentType["UtilityBill"] = "utility_bill";
})(UploadedDocumentType || (UploadedDocumentType = {}));
/**
 * The side of the document (front or back)
 */
export var DocumentSide;
(function (DocumentSide) {
    DocumentSide["Back"] = "back";
    DocumentSide["Front"] = "front";
})(DocumentSide || (DocumentSide = {}));
/**
 * The information that the user must give us.
 */
export var RequiredActionsName;
(function (RequiredActionsName) {
    RequiredActionsName["BorderlessCddRemediation"] = "borderless_cdd_remediation";
    RequiredActionsName["FillAchDetails"] = "fill_ach_details";
    RequiredActionsName["ProvideAddressConfirmation"] = "provide_address_confirmation";
    RequiredActionsName["ProvideAnyAddressConfirmation"] = "provide_any_address_confirmation";
    RequiredActionsName["ProvideAnyIdentityConfirmation"] = "provide_any_identity_confirmation";
    RequiredActionsName["ProvideBankStatement"] = "provide_bank_statement";
    RequiredActionsName["ProvideCharityConfirmation"] = "provide_charity_confirmation";
    RequiredActionsName["ProvideCompanyConfirmation"] = "provide_company_confirmation";
    RequiredActionsName["ProvideCompanyUbosConfirmation"] = "provide_company_ubos_confirmation";
    RequiredActionsName["ProvideGcSasDocuments"] = "provide_gc_sas_documents";
    RequiredActionsName["ProvideHighRiskGcSasDocuments"] = "provide_high_risk_gc_sas_documents";
    RequiredActionsName["ProvideIdentityConfirmation"] = "provide_identity_confirmation";
    RequiredActionsName["ProvidePartnersConfirmation"] = "provide_partners_confirmation";
    RequiredActionsName["ProvidePartnershipConfirmation"] = "provide_partnership_confirmation";
    RequiredActionsName["ProvidePrimaryDirectorConfirmation"] = "provide_primary_director_confirmation";
    RequiredActionsName["ProvidePrimaryUserConfirmation"] = "provide_primary_user_confirmation";
    RequiredActionsName["ProvideSecondaryUserConfirmation"] = "provide_secondary_user_confirmation";
    RequiredActionsName["ProvideSupportQuestions"] = "provide_support_questions";
    RequiredActionsName["ProvideTrustConfirmation"] = "provide_trust_confirmation";
    RequiredActionsName["ProvideUbosConfirmation"] = "provide_ubos_confirmation";
    RequiredActionsName["SendVerificationDeposit"] = "send_verification_deposit";
})(RequiredActionsName || (RequiredActionsName = {}));
/**
 * The current status of the action
 */
export var RequiredActionsStatus;
(function (RequiredActionsStatus) {
    RequiredActionsStatus["Cancelled"] = "cancelled";
    RequiredActionsStatus["CouldNotVerify"] = "could_not_verify";
    RequiredActionsStatus["InAutomaticReview"] = "in_automatic_review";
    RequiredActionsStatus["InReview"] = "in_review";
    RequiredActionsStatus["Pending"] = "pending";
    RequiredActionsStatus["PendingResponse"] = "pending_response";
    RequiredActionsStatus["Processing"] = "processing";
    RequiredActionsStatus["Successful"] = "successful";
    RequiredActionsStatus["Superseded"] = "superseded";
    RequiredActionsStatus["Unsuccessful"] = "unsuccessful";
})(RequiredActionsStatus || (RequiredActionsStatus = {}));
export var Error;
(function (Error) {
    Error["InvalidDocumentType"] = "invalid_document_type";
    Error["InvalidIssueDate"] = "invalid_issue_date";
    Error["Other"] = "other";
})(Error || (Error = {}));
export var RequiredActionCheckDocumentResponseBodyResult;
(function (RequiredActionCheckDocumentResponseBodyResult) {
    RequiredActionCheckDocumentResponseBodyResult["Invalid"] = "invalid";
    RequiredActionCheckDocumentResponseBodyResult["Unknown"] = "unknown";
    RequiredActionCheckDocumentResponseBodyResult["Valid"] = "valid";
})(RequiredActionCheckDocumentResponseBodyResult || (RequiredActionCheckDocumentResponseBodyResult = {}));
/**
 * The verification name.
 */
export var RequiredVerificationName;
(function (RequiredVerificationName) {
    RequiredVerificationName["AddCreditorBankAccount"] = "add_creditor_bank_account";
    RequiredVerificationName["CheckCompany"] = "check_company";
    RequiredVerificationName["CheckPerson"] = "check_person";
    RequiredVerificationName["ContactSupport"] = "contact_support";
    RequiredVerificationName["FillCreditorDetails"] = "fill_creditor_details";
    RequiredVerificationName["ManualCheck"] = "manual_check";
    RequiredVerificationName["ProvideFurtherInformation"] = "provide_further_information";
})(RequiredVerificationName || (RequiredVerificationName = {}));
export var Reason;
(function (Reason) {
    Reason["CheckBankAccount"] = "check_bank_account";
    Reason["CheckCompany"] = "check_company";
    Reason["CheckPerson"] = "check_person";
    Reason["CheckPersonAddress"] = "check_person_address";
    Reason["CheckPersonIdentity"] = "check_person_identity";
    Reason["CompanyDirector"] = "company_director";
})(Reason || (Reason = {}));
/**
 * The current status of the verification
 */
export var RequiredVerificationStatus;
(function (RequiredVerificationStatus) {
    RequiredVerificationStatus["InReview"] = "in_review";
    RequiredVerificationStatus["Pending"] = "pending";
    RequiredVerificationStatus["Processing"] = "processing";
})(RequiredVerificationStatus || (RequiredVerificationStatus = {}));
/**
 * Whether the "Rate your experience" feedback dialog should be shown to a user in the FPJ
 * experiment.
 */
export var FpjFeedbackDialogState;
(function (FpjFeedbackDialogState) {
    FpjFeedbackDialogState["Completed"] = "completed";
    FpjFeedbackDialogState["Null"] = "null";
    FpjFeedbackDialogState["Show"] = "show";
})(FpjFeedbackDialogState || (FpjFeedbackDialogState = {}));
/**
 * Permission scope
 */
export var UsersScope;
(function (UsersScope) {
    UsersScope["Admin"] = "admin";
    UsersScope["NoAccess"] = "no_access";
    UsersScope["ReadOnly"] = "read_only";
    UsersScope["ReadWrite"] = "read_write";
})(UsersScope || (UsersScope = {}));
/**
 * Unique identifier for the type of upgrade
 */
export var UpgradeType;
(function (UpgradeType) {
    UpgradeType["CanCreateBulkChanges"] = "can_create_bulk_changes";
    UpgradeType["CustomNotifications"] = "custom_notifications";
    UpgradeType["CustomPaymentPages"] = "custom_payment_pages";
    UpgradeType["DashboardMandateCreation"] = "dashboard_mandate_creation";
    UpgradeType["DisableSaferRefunds"] = "disable_safer_refunds";
    UpgradeType["EncryptedBankDetailsAccess"] = "encrypted_bank_details_access";
    UpgradeType["ErasmusExperimental"] = "erasmus_experimental";
    UpgradeType["IbpRetries"] = "ibp_retries";
    UpgradeType["IntelligentNotifications"] = "intelligent_notifications";
    UpgradeType["MandateRefunds"] = "mandate_refunds";
    UpgradeType["OutOfFlowOfFunds"] = "out_of_flow_of_funds";
    UpgradeType["OwnSun"] = "own_sun";
    UpgradeType["PaymentSuccessScores"] = "payment_success_scores";
    UpgradeType["ProtectPlus"] = "protect_plus";
    UpgradeType["ProtectPlusBlocklist"] = "protect_plus_blocklist";
    UpgradeType["ProtectPlusChargebackChallenge"] = "protect_plus_chargeback_challenge";
    UpgradeType["ProtectPlusDetectVerify"] = "protect_plus_detect_verify";
    UpgradeType["SsoAvailable"] = "sso_available";
    UpgradeType["SuccessPlus"] = "success_plus";
    UpgradeType["TransferBankAccounts"] = "transfer_bank_accounts";
    UpgradeType["UiComponents"] = "ui_components";
    UpgradeType["UsachSameDayAllPayments"] = "usach_same_day_all_payments";
    UpgradeType["UsachSameDayPayments"] = "usach_same_day_payments";
    UpgradeType["VerifiedMandates"] = "verified_mandates";
    UpgradeType["VrpSweeping"] = "vrp_sweeping";
    UpgradeType["WhitelabelPartner"] = "whitelabel_partner";
})(UpgradeType || (UpgradeType = {}));
/**
 * The current status of the upgrade
 */
export var UpgradesStatus;
(function (UpgradesStatus) {
    UpgradesStatus["Active"] = "active";
    UpgradesStatus["InReview"] = "in_review";
    UpgradesStatus["Inactive"] = "inactive";
})(UpgradesStatus || (UpgradesStatus = {}));
/**
 * List of resource types that we'll send webhooks about.
 */
export var SubscribedResource;
(function (SubscribedResource) {
    SubscribedResource["BillingRequest"] = "billing_request";
    SubscribedResource["Creditor"] = "creditor";
    SubscribedResource["Export"] = "export";
    SubscribedResource["InstalmentSchedule"] = "instalment_schedule";
    SubscribedResource["Mandate"] = "mandate";
    SubscribedResource["OutboundPayment"] = "outbound_payment";
    SubscribedResource["PayerAuthorisation"] = "payer_authorisation";
    SubscribedResource["Payment"] = "payment";
    SubscribedResource["Payout"] = "payout";
    SubscribedResource["Refund"] = "refund";
    SubscribedResource["SchemeIdentifier"] = "scheme_identifier";
    SubscribedResource["Subscription"] = "subscription";
})(SubscribedResource || (SubscribedResource = {}));
